import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { safeHref } from '../../utils/common/safe-url';
import {
  adminDecideLiveReport,
  adminGetLiveReport,
  adminListLiveReports,
  AdminBrandLiveReport,
} from '../../api/payment/brand-live.api';
import { hoursLabel, liveVoidReasonLabel, validateReason } from '../../utils/brand-live';
import AdminLiveDecisionModal from './admin-live-decision-modal';
import { AdminOrdererLabel } from '../brand-deal/orderer-badge';

// Tab "Báo gian lận livestream": cũ nhất lên đầu. Admin chỉ quyết khi creator đã giải trình hoặc đã hết hạn giải trình.

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;
const fmtTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('vi-VN') : '');

const canDecide = (r: AdminBrandLiveReport, now = Date.now()) =>
  !!r.creatorResponse || new Date(r.responseDueAt).getTime() <= now;

interface Modal {
  report: AdminBrandLiveReport;
  upheld: boolean;
  stopDeal: boolean;
  reason: string;
  totp: string;
}

const AdminLiveReports: React.FC<{ onCount?: (n: number) => void }> = ({ onCount }) => {
  const [items, setItems] = useState<AdminBrandLiveReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<Record<string, AdminBrandLiveReport>>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [modal, setModal] = useState<Modal | null>(null);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);

  const load = useCallback(async () => {
    try {
      const list = await adminListLiveReports('Open');
      setItems(list);
      onCount?.(list.length);
    } catch (e) {
      toast.error(errorMessage(e, 'Không tải được danh sách.'));
    } finally {
      setLoading(false);
    }
  }, [onCount]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (id: string) => {
    if (openId === id) return setOpenId(null);
    setOpenId(id);
    if (detail[id]) return;
    try {
      const d = await adminGetLiveReport(id);
      setDetail((p) => ({ ...p, [id]: d }));
    } catch (e) {
      setOpenId((cur) => (cur === id ? null : cur)); // đóng panel để bấm mở lại được, không kẹt "Đang tải…"
      toast.error(errorMessage(e, 'Không tải được nhật ký phút.'));
    }
  };

  const confirm = async () => {
    if (!modal || lock.current) return;
    const invalid = validateReason(modal.reason);
    if (invalid) return toast.error(invalid);
    if (modal.totp.length !== 6) return toast.error('Cần mã Authenticator 6 số.');
    lock.current = true;
    setBusy(true);
    try {
      await adminDecideLiveReport(modal.report.id, modal.totp, {
        upheld: modal.upheld,
        stopDeal: modal.upheld && modal.stopDeal,
        reason: modal.reason.trim(),
      });
      toast.success(modal.upheld ? 'Đã chấp nhận báo cáo.' : 'Đã bác bỏ báo cáo.');
      setModal(null);
      await load();
    } catch (e) {
      toast.error(errorMessage(e, 'Mã Authenticator không hợp lệ hoặc xử lý thất bại.'));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  if (loading) return <p className="text-gray-500">Đang tải…</p>;
  if (items.length === 0) return <p className="text-gray-500">Không có báo cáo gian lận nào đang chờ.</p>;

  return (
    <div className="space-y-3">
      <p className="text-sm text-amber-600 dark:text-amber-400">
        Có {items.length} báo cáo đang chờ. Phần tiền của buổi này được giữ lại cho đến khi bạn quyết.
      </p>
      {items.map((r) => {
        const d = detail[r.id];
        const ready = canDecide(r);
        return (
          <div key={r.id} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-boxdark">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 text-sm">
                <div className="font-semibold dark:text-white">
                  <AdminOrdererLabel name={r.businessName} kind={r.ordererKind} ownerIdentityName={r.ownerIdentityName} /> <span className="font-normal text-gray-500">báo creator {r.creatorName}</span>
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      ready
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {ready ? 'Sẵn sàng quyết' : `Creator còn hạn giải trình đến ${fmtTime(r.responseDueAt)}`}
                  </span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Buổi live {fmtTime(r.sessionStartedAt)}
                  {r.sessionEndedAt ? ` đến ${fmtTime(r.sessionEndedAt)}` : ''} · Quảng cáo phát {hoursLabel(r.sessionSeconds)} ·
                  Báo lúc {fmtTime(r.createdAt)}
                </div>
                {r.liveUrl && (
                  <a
                    href={safeHref(r.liveUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Mở link buổi live
                  </a>
                )}
                <div className="mt-1 whitespace-pre-wrap text-gray-700 dark:text-gray-200">
                  <b>Lý do báo:</b> {r.reason}
                </div>
                <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-200">
                  <b>Creator giải trình:</b> {r.creatorResponse || 'Chưa có giải trình.'}
                </div>
                <button
                  type="button"
                  onClick={() => toggle(r.id)}
                  className="mt-1 text-xs font-semibold text-blue-600 hover:underline"
                >
                  {openId === r.id ? 'Ẩn nhật ký từng phút' : 'Xem nhật ký từng phút'}
                </button>
              </div>
              <div className="flex shrink-0 flex-col gap-2">
                <button
                  disabled={!ready}
                  onClick={() => setModal({ report: r, upheld: true, stopDeal: false, reason: '', totp: '' })}
                  className="rounded bg-rose-600 px-3 py-1.5 text-sm text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Chấp nhận báo cáo
                </button>
                <button
                  disabled={!ready}
                  onClick={() => setModal({ report: r, upheld: false, stopDeal: false, reason: '', totp: '' })}
                  className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Bác bỏ báo cáo
                </button>
              </div>
            </div>
            {openId === r.id && (
              <div className="mt-3 max-h-72 overflow-auto rounded border border-gray-200 dark:border-gray-700">
                {!d ? (
                  <p className="p-3 text-xs text-gray-500">Đang tải…</p>
                ) : !d.minutes || d.minutes.length === 0 ? (
                  <p className="p-3 text-xs text-gray-500">Buổi này chưa có phút nào được ghi.</p>
                ) : (
                  <table className="w-full text-left text-xs dark:text-gray-200">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-3 py-1.5">Phút</th>
                        <th className="px-3 py-1.5">Số giây</th>
                        <th className="px-3 py-1.5">Đã huỷ chưa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.minutes.map((m) => (
                        <tr key={m.minuteStart} className="border-t border-gray-100 dark:border-gray-700">
                          <td className="px-3 py-1">{fmtTime(m.minuteStart)}</td>
                          <td className="px-3 py-1">{m.seconds}</td>
                          <td className="px-3 py-1">
                            {m.voidedAt ? `Đã huỷ ${fmtTime(m.voidedAt)}${m.voidReason ? ` (${liveVoidReasonLabel(m.voidReason)})` : ''}` : 'Chưa'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
      {modal && (
        <AdminLiveDecisionModal
          title={modal.upheld ? 'Chấp nhận báo cáo gian lận' : 'Bác bỏ báo cáo gian lận'}
          description={
            modal.upheld
              ? 'Các phút của buổi này bị huỷ, giờ phát bị trừ lại và creator không được trả cho buổi này.'
              : 'Báo cáo bị bác bỏ, phần tiền của buổi này được trả theo lịch thường.'
          }
          reason={modal.reason}
          totp={modal.totp}
          busy={busy}
          onReason={(v) => setModal((p) => (p ? { ...p, reason: v } : p))}
          onTotp={(v) => setModal((p) => (p ? { ...p, totp: v } : p))}
          onClose={() => setModal(null)}
          onConfirm={confirm}
        >
          {modal.upheld && (
            <label className="mb-4 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={modal.stopDeal}
                onChange={(e) => setModal((p) => (p ? { ...p, stopDeal: e.target.checked } : p))}
              />
              Dừng deal
            </label>
          )}
        </AdminLiveDecisionModal>
      )}
    </div>
  );
};

export default AdminLiveReports;
